import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { R, shadow } from '../lib/ui';
import { AnchoredPopover } from './AnchoredPopover';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
  icon?: ReactNode;
}

/**
 * Свой выпадающий список: у системного select нельзя покрасить панель
 * и показать иконки. Стрелка поворачивается при открытии.
 */
export function Select<T extends string>({
  value, options, onChange, placeholder = 'Выбрать…', disabled, compact, width, style,
}: {
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
  width?: number | string;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const current = options.find((o) => o.value === value);

  return (
    <div style={{ position: 'relative', width: width ?? '100%' }}>
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          width: '100%',
          padding: compact ? '5px 28px 5px 8px' : '9px 32px 9px 12px',
          textAlign: 'left',
          fontSize: compact ? 12 : 13,
          border: `1px solid ${open ? R.accentBorder : R.border}`,
          background: R.bgInput,
          color: current ? R.text : R.textMuted,
          cursor: disabled ? 'default' : 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          ...style,
        }}
      >
        {current?.icon}
        <span
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {current?.label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            transition: 'transform .12s',
            color: R.textMuted,
          }}
        />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          role="listbox"
          className="rt-scroll"
          style={{
            minWidth: ref.current?.offsetWidth ?? 200,
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            boxShadow: shadow.dropdown,
            padding: '4px 0',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '7px 10px',
                  textAlign: 'left',
                  fontSize: 13,
                  color: selected ? R.accent : R.text,
                  fontWeight: selected ? 600 : 400,
                  background: selected ? R.accentSubtle : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!selected) e.currentTarget.style.background = R.bgElevated;
                }}
                onMouseLeave={(e) => {
                  if (!selected) e.currentTarget.style.background = 'transparent';
                }}
              >
                {option.icon}
                <span style={{ flex: 1 }}>{option.label}</span>
                {option.hint && (
                  <span style={{ fontSize: 11, color: R.textMuted }}>{option.hint}</span>
                )}
              </button>
            );
          })}
        </div>
      </AnchoredPopover>
    </div>
  );
}
